export const REQUESTED_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: 1,
};

export interface CaptureDiagnostics {
  contextSampleRate: number;
  trackSampleRate?: number;
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
  channelCount?: number;
  warnings: string[];
}

export function describeSettings(settings: MediaTrackSettings, contextSampleRate: number): CaptureDiagnostics {
  const warnings: string[] = [];
  if (settings.autoGainControl === true) {
    warnings.push('Prohlížeč nechal zapnuté automatické vyrovnání hlasitosti (AGC). Onsety mohou být zkreslené.');
  }
  if (settings.autoGainControl === undefined) {
    warnings.push('Prohlížeč nehlásí stav AGC. Může být zapnuté.');
  }
  // Some browsers report echoCancellation as a mode string (e.g. "all", "remote-only")
  // instead of a boolean. Any such string means it's active.
  let echoCancellation: boolean | undefined;
  if (typeof settings.echoCancellation === 'string') {
    echoCancellation = true;
    warnings.push(`Potlačení ozvěny je zapnuté (režim „${settings.echoCancellation}“).`);
  } else {
    echoCancellation = settings.echoCancellation;
    if (echoCancellation === true) warnings.push('Potlačení ozvěny je zapnuté.');
  }
  if (settings.noiseSuppression === true) warnings.push('Potlačení šumu je zapnuté.');
  if (settings.sampleRate !== undefined && settings.sampleRate !== contextSampleRate) {
    warnings.push(`Stopa běží na ${settings.sampleRate} Hz, AudioContext na ${contextSampleRate} Hz. Prohlížeč převzorkovává.`);
  }
  return {
    contextSampleRate,
    trackSampleRate: settings.sampleRate,
    echoCancellation,
    noiseSuppression: settings.noiseSuppression,
    autoGainControl: settings.autoGainControl,
    channelCount: settings.channelCount,
    warnings,
  };
}

export type CaptureErrorKind = 'denied' | 'no-device' | 'insecure-context' | 'unsupported' | 'unknown';

export function classifyCaptureError(err: unknown): CaptureErrorKind {
  const name = typeof err === 'object' && err !== null ? (err as { name?: unknown }).name : undefined;
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'no-device';
    case 'InsecureContextError':
      return 'insecure-context';
    case 'UnsupportedError':
      return 'unsupported';
    default:
      return 'unknown';
  }
}

export interface Capture {
  context: AudioContext;
  stream: MediaStream;
  track: MediaStreamTrack;
  source: MediaStreamAudioSourceNode;
  diagnostics: CaptureDiagnostics;
}

/** Musí se volat synchronně v obsluze klepnutí. iOS Safari jinak kontext nespustí (spec 7.1). */
export function createAudioContext(): AudioContext {
  configureAudioSession();
  const context = new AudioContext({ latencyHint: 'interactive' });
  // Resume musí proběhnout synchronně v obsluze klepnutí, jinak ho iOS Safari zablokuje (spec 7.1).
  void context.resume();
  return context;
}

function configureAudioSession(): void {
  const nav = navigator as Navigator & { audioSession?: { type: string } };
  if (!nav.audioSession) return;
  try {
    nav.audioSession.type = 'play-and-record';
  } catch {
    // starší implementace hodnotu odmítne; bez ní se pokračuje
  }
}

function namedError(name: string, message: string): Error {
  return Object.assign(new Error(message), { name });
}

export async function startCapture(context: AudioContext): Promise<Capture> {
  if (!window.isSecureContext) throw namedError('InsecureContextError', 'getUserMedia vyžaduje HTTPS');
  if (!navigator.mediaDevices?.getUserMedia) throw namedError('UnsupportedError', 'getUserMedia není k dispozici');
  const stream = await navigator.mediaDevices.getUserMedia({ audio: REQUESTED_CONSTRAINTS, video: false });
  const track = stream.getAudioTracks()[0];
  if (!track) throw namedError('NotFoundError', 'stream nemá audio stopu');
  if (context.state !== 'running') await context.resume();
  const source = context.createMediaStreamSource(stream);
  const diagnostics = describeSettings(track.getSettings(), context.sampleRate);
  return { context, stream, track, source, diagnostics };
}

export function stopCapture(capture: Capture): void {
  capture.track.stop();
  capture.source.disconnect();
  void capture.context.close();
}
